import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useDispatch } from 'react-redux'; 
import { setExpertCode, setRegistrationData } from '@/store/slices/expertSlice'; 
import { getOnboardingStatus } from '@/api/onboardingApis/onboarding.api';
import type { OnboardingStatus } from '@/api/onboardingApis/onboarding.api';
import { IS_DEV, TEST_REAL_API } from "@/config/env"; // ✅ Updated Import

// 🔴 REMOVED HARDCODED CONSTANT - NOW IMPORTED FROM CONFIG 🔴

const OnboardingTracker = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const trackUserProgress = async () => {
      try {
        const expertCode = localStorage.getItem('expertCode');
        
        if (!expertCode) {
           console.warn("Tracker: No expertCode found. Redirecting to login.");
           navigate('/expert-login');
           return;
        }

        let data: OnboardingStatus;

        // 🟢 MOCK LOGIC
        // Now uses the TEST_REAL_API value from your config file
        if (IS_DEV && !TEST_REAL_API && expertCode === 'EXP-MOCK-DEV') {
       
          await new Promise(resolve => setTimeout(resolve, 500));

          const storedCatId = localStorage.getItem('dev_mock_categoryId');
          // Default to Doctor (3) if missing
          const mockCategoryId = storedCatId ? Number(storedCatId) : 3; 

      

          // 2. Generate Mock Data
          data = {
            expert_code: "EXP-MOCK-DEV",
            full_name: "Dev User",
            email: "dev@example.com",
            phone: "9999999999",
            
            // 🔥 FIX: Convert number to string to match OnboardingStatus type
            category_id: String(mockCategoryId), 
            
            account_status: 'in_progress',

            // Added missing required property
            is_registration_step1_completed: true, 
            
            // Astrology
            is_astrology_final_step_completed: false,
            
            // Doctor
            is_doctor_step2_completed: false,
            is_doctor_step3_completed: false,
            is_doctor_step4_completed: false,
            is_doctor_step5_completed: false,
            is_doctor_final_step_completed: false,
            
            // Lawyer
            is_lawyer_step2_completed: false, 
            is_lawyer_final_step_completed: false,
          } as OnboardingStatus;

        } else {
          // 🔴 REAL API CALL
          const response = await getOnboardingStatus(expertCode);
          data = response.data;
        }

        // DISPATCH
        if (data) {
          dispatch(setExpertCode(data.expert_code || expertCode));
          dispatch(setRegistrationData({
             fullName: data.full_name,
             email: data.email,
             phone: data.phone || '',
             categoryId: String(data.category_id), 
             categoryName: '', 
             subCategoryId: '', 
             subCategoryName: ''
          }));
        }

        const destination = determineNextStep(data);
     
        navigate(destination, { replace: true });

      } catch (error) {
        console.error("Tracker: Sync failed", error);
        if (!IS_DEV) navigate('/expert-login'); 
      }
    };

    trackUserProgress();
  }, [navigate, dispatch]);

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center animate-pulse">
         <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
         <h2 className="text-gray-600 font-medium text-lg">Syncing your progress...</h2>
         <p className="text-gray-400 text-sm mt-2">Checking your onboarding status</p>
         {IS_DEV && !TEST_REAL_API && (
            <span className="mt-4 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded border border-yellow-300">
               Dev Mock Mode Active
            </span>
         )}
      </div>
    </div>
  );
};

// --- LOGIC TO MATCH YOUR DB ---
function determineNextStep(status: OnboardingStatus): string {
  const catId = String(status.category_id); 
  let category = '';

  // 1. Identify Category
  if (catId === '1') category = 'astrology';
  else if (catId === '2') category = 'lawyer';
  else if (catId === '3') category = 'doctor';
  else category = 'doctor'; 

  const isPending = status.account_status === 'pending_review';

  // 3. Logic Per Category
  switch (category) {
    case 'astrology':
       if (status.is_astrology_final_step_completed) {
         return isPending ? `/expert/application-submitted` : `/expert/dashboard`;
       }
       return `/expert-onboarding/astrology`;

    case 'doctor':
      if (!status.is_doctor_step2_completed) return `/expert-onboarding/doctor?step=2`;
      if (!status.is_doctor_step3_completed) return `/expert-onboarding/doctor?step=3`;
      if (!status.is_doctor_step4_completed) return `/expert-onboarding/doctor?step=4`;
      if (!status.is_doctor_step5_completed) return `/expert-onboarding/doctor?step=5`;
      if (status.is_doctor_final_step_completed) {
         return isPending ? `/expert/application-submitted` : `/expert/dashboard`;
      }
      return `/expert-onboarding/doctor?step=6`;

    case 'lawyer':
       // 1. If Final Step is done -> Success/Dashboard
       if (status.is_lawyer_final_step_completed) {
         return isPending ? `/expert/application-submitted` : `/expert/dashboard`;
       }
       
       // 2. If Step 2 is done -> Go to Final Step (Step 3)
       if (status.is_lawyer_step2_completed) {
          return `/expert-onboarding/lawyer?step=3`;
       }

       // 3. Otherwise -> Go to Step 2
       return `/expert-onboarding/lawyer?step=2`;

    default:
      return `/expert-onboarding/${category}`;
  }
}

export default OnboardingTracker;